import { useState } from "react";
import { Drawer, Segmented } from "antd";
import { BarChartOutlined, LineChartOutlined } from "@ant-design/icons";

import { SpirometryViewer } from "./SpirometryViewer";
import { SpirometryComparisonViewer } from "./SpirometryComparisonViewer";

const DRAWER_WIDTH = "85%";

// Placeholder NCT ID passed to the comparison viewer. The useFetch hook
// resolves the actual trial from the URL routing context, so this value is
// used only for display/future reference.
const UNKNOWN_NCT_ID = "";

export interface SpirometryDrawerEffortMeta {
  subject_id?: string | null;
  collection_date?: string | null;
}

export interface SpirometryDrawerProps {
  open: boolean;
  onClose: () => void;
  transmittalNumber: number | null;
  effortMeta?: SpirometryDrawerEffortMeta;
}

type ViewMode = "single" | "compare";

const buildTitle = (
  transmittalNumber: number | null,
  effortMeta?: SpirometryDrawerEffortMeta,
) => {
  const parts = [
    effortMeta?.subject_id ? `subject ${effortMeta.subject_id}` : null,
    effortMeta?.collection_date,
  ].filter(Boolean);
  if (parts.length) return `Spirometry — ${parts.join(" · ")}`;
  return transmittalNumber != null
    ? `Spirometry — transmittal ${transmittalNumber}`
    : "Spirometry";
};

export const SpirometryDrawer = ({
  open,
  onClose,
  transmittalNumber,
  effortMeta,
}: SpirometryDrawerProps) => {
  const [viewMode, setViewMode] = useState<ViewMode>("single");

  // When the drawer closes, reset back to single-subject view.
  const handleClose = () => {
    setViewMode("single");
    onClose();
  };

  const currentSubjectId = effortMeta?.subject_id ?? null;

  // Initial subject list for the comparison selector. We seed it with the
  // currently-viewed subject; the viewer fetches all available subjects
  // internally for the full multi-select list.
  const initialSubjectIds = currentSubjectId ? [currentSubjectId] : [];

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      width={DRAWER_WIDTH}
      destroyOnHidden
      title={
        <div className="flex items-center gap-4">
          <span>{buildTitle(transmittalNumber, effortMeta)}</span>
          <Segmented<ViewMode>
            size="small"
            value={viewMode}
            onChange={setViewMode}
            options={[
              {
                label: (
                  <span className="flex items-center gap-1">
                    <LineChartOutlined />
                    Single subject
                  </span>
                ),
                value: "single",
              },
              {
                label: (
                  <span className="flex items-center gap-1">
                    <BarChartOutlined />
                    Compare
                  </span>
                ),
                value: "compare",
              },
            ]}
          />
        </div>
      }
    >
      {open && (
        <>
          {viewMode === "single" && transmittalNumber != null && (
            <SpirometryViewer transmittalNumber={transmittalNumber} />
          )}
          {viewMode === "compare" && (
            <SpirometryComparisonViewer
              nctId={UNKNOWN_NCT_ID}
              subjectIds={initialSubjectIds}
            />
          )}
        </>
      )}
    </Drawer>
  );
};
